import {
  DeclineReason,
  Filing,
  FilingOption,
  NewHospital,
  NewHospitalAddress,
  NewHospitalStatus,
  NewHospitalTab,
  newHospitalWriteError,
} from 'services/api/admin/newHospitals';
import { validationErrorMessage } from 'services/api/validationErrors';

export type Tone = 'blue' | 'red' | 'amber' | 'neutral' | 'green';

export const STATUS: Record<NewHospitalStatus, { label: string; tone: Tone }> = {
  ready: { label: 'Ready', tone: 'green' },
  pick_client: { label: 'Pick a client', tone: 'amber' },
  which_hospital: { label: 'Which hospital?', tone: 'blue' },
  created: { label: 'Created', tone: 'neutral' },
  declined: { label: 'Declined', tone: 'red' },
};

export const DECLINE_LABEL: Record<DeclineReason, string> = {
  existing_group: "It's an existing Olio hospital",
  not_hospital: 'Not a hospital',
  dont_create: "Don't create it",
};

export const TABS: { label: string; value: NewHospitalTab }[] = [
  { label: 'Needs review', value: 'review' },
  { label: 'Created', value: 'created' },
  { label: 'Declined', value: 'declined' },
];

export function addressLine(address: NewHospitalAddress | null | undefined) {
  if (!address) return '';
  const stateZip = [address.state, address.zip].filter(Boolean).join(' ');
  return [address.streetAddress, address.city, stateZip].filter(Boolean).join(', ');
}

export function cityState(address: NewHospitalAddress | null | undefined) {
  return [address?.city, address?.state].filter(Boolean).join(', ');
}

export function optionLabel(option: FilingOption | undefined) {
  if (!option) return '';
  return option.kind === 'existing_client' ? option.clientName : option.proposedName;
}

export function optionForFiling(options: FilingOption[], filing: Filing | null) {
  if (!filing) return undefined;
  return options.find((o) =>
    filing.kind === 'existing_client'
      ? o.kind === 'existing_client' && o.clientId === filing.clientId
      : o.kind === 'new_client'
  );
}

export function filingLabel(row: NewHospital) {
  const filing = row.filing;
  if (!filing) return '';
  if (filing.kind === 'new_client') return filing.name;
  const option = optionForFiling(row.filingOptions, filing);
  return option ? optionLabel(option) : '';
}

export function confidence(value: number | null | undefined) {
  return value == null ? '' : value.toFixed(2);
}

export function shortDate(value: string | null | undefined) {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString();
}

const DRAFT_CHANGED = 'This draft changed. Reload to see the latest.';

export function writeErrorMessage(error: unknown) {
  const { code, message } = newHospitalWriteError(error);
  switch (code) {
    case 'draft_changed':
      return DRAFT_CHANGED;
    case 'client_has_users':
      return 'That client has users now, so it can’t hold this hospital. Reload and pick another.';
    case 'hospital_exists':
      return message ?? 'An Olio hospital already carries this CCN.';
    case 'filing_changed':
      return message ?? 'Where this files changed. It has been reloaded.';
    default:
      return validationErrorMessage(error) ?? message ?? 'Something went wrong. Please try again.';
  }
}
